import { useEffect, useRef, useState } from 'react';
import { safeFetch } from '../../../lib/api';

/**
 * Server-side email draft persistence (collection: emailDrafts).
 *
 * 'broadcast' and 'direct' drafts are strictly owner-scoped: the server
 * keys them by the caller's uid + workspace + kind + contextId, so an
 * admin only ever sees their own unfinished work. 'campaign' drafts are
 * SHARED across the workspace (PR-11 §B): every admin with the email
 * campaign permission sees the team's drafts for a batch, with owner /
 * last-editor attribution, and resuming one updates that same shared
 * doc (via its draft id) instead of forking a private copy.
 *
 *   kind 'campaign'  → Batch Center 4-step campaign wizard (contextId = batchId)
 *   kind 'broadcast' → Batch Center quick broadcast panel   (contextId = batchId)
 *   kind 'direct'    → Applicant EmailTab composer          (contextId = applicationId)
 *
 * Contract (server.ts):
 *   PUT    /api/email/drafts            { kind, contextId, data, draftId? } → { updatedAt, draftId }
 *   GET    /api/email/drafts?kind&contextId                       → { draft | null }
 *   GET    /api/email/drafts/list?kind&contextId?                 → { drafts: [...] }
 *   DELETE /api/email/drafts?kind&contextId&draftId?              → { success: true }
 */

export type EmailDraftKind = 'campaign' | 'broadcast' | 'direct';

export interface EmailDraftRecord {
  /** Server doc id — the shared-draft identity for kind 'campaign'. */
  id?: string | null;
  kind: EmailDraftKind;
  contextId: string;
  data: Record<string, any>;
  updatedAt: string;
  ownerUid?: string | null;
  ownerName?: string;
  ownerEmail?: string;
  lastEditorUid?: string | null;
  lastEditorName?: string;
  lastEditorEmail?: string;
  /** True when the draft's owner is the calling admin. */
  mine?: boolean;
}

export async function loadEmailDraft(
  kind: EmailDraftKind,
  contextId: string,
): Promise<EmailDraftRecord | null> {
  const res: any = await safeFetch(
    `/api/email/drafts?kind=${encodeURIComponent(kind)}&contextId=${encodeURIComponent(contextId)}`,
  );
  return (res?.draft as EmailDraftRecord) || null;
}

export async function saveEmailDraft(
  kind: EmailDraftKind,
  contextId: string,
  data: Record<string, any>,
  draftId?: string | null,
): Promise<{ updatedAt: string; draftId?: string | null; draft?: EmailDraftRecord | null }> {
  const res: any = await safeFetch('/api/email/drafts', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind, contextId, data, ...(draftId ? { draftId } : {}) }),
  });
  return {
    updatedAt: res?.updatedAt || new Date().toISOString(),
    draftId: res?.draftId ?? draftId ?? null,
    draft: (res?.draft as EmailDraftRecord) || null,
  };
}

export async function deleteEmailDraft(
  kind: EmailDraftKind,
  contextId: string,
  draftId?: string | null,
): Promise<void> {
  const target = draftId ? `&draftId=${encodeURIComponent(draftId)}` : '';
  await safeFetch(
    `/api/email/drafts?kind=${encodeURIComponent(kind)}&contextId=${encodeURIComponent(contextId)}${target}`,
    { method: 'DELETE' },
  );
}

export async function listEmailDrafts(kind: EmailDraftKind): Promise<EmailDraftRecord[]> {
  const res: any = await safeFetch(`/api/email/drafts/list?kind=${encodeURIComponent(kind)}`);
  return Array.isArray(res?.drafts) ? res.drafts : [];
}

/** All workspace campaign drafts for one batch (the Team Drafts panel). */
export async function listCampaignDrafts(contextId: string): Promise<EmailDraftRecord[]> {
  const res: any = await safeFetch(
    `/api/email/drafts/list?kind=campaign&contextId=${encodeURIComponent(contextId)}`,
  );
  const drafts: EmailDraftRecord[] = Array.isArray(res?.drafts) ? res.drafts : [];
  return drafts.filter((d) => d.contextId === contextId);
}

/** Display label for a draft's author: "You" for the caller's own draft. */
export function draftAuthorLabel(draft: Pick<EmailDraftRecord, 'ownerName' | 'ownerEmail' | 'mine'>): string {
  if (draft.mine) return 'you';
  return draft.ownerName || draft.ownerEmail || 'a teammate';
}

/** Display label for a draft's most recent editor. */
export function draftLastEditorLabel(
  draft: Pick<
    EmailDraftRecord,
    'lastEditorName' | 'lastEditorEmail' | 'lastEditorUid' | 'ownerUid' | 'ownerName' | 'ownerEmail' | 'mine'
  >,
): string {
  if (draft.mine && draft.lastEditorUid && draft.ownerUid && draft.lastEditorUid !== draft.ownerUid) {
    return draft.lastEditorName || draft.lastEditorEmail || 'a teammate';
  }
  if (draft.mine) return 'you';
  return draft.lastEditorName || draft.lastEditorEmail || draft.ownerName || 'a teammate';
}

/** "3:42 PM" style label for the autosave indicator. */
export function formatDraftTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export type EmailDraftStatus = 'idle' | 'loading' | 'saving' | 'saved' | 'error';

export interface UseEmailDraftResult {
  status: EmailDraftStatus;
  /** ISO timestamp of the last successful save (or of the restored draft). */
  savedAt: string | null;
  /** True when a server draft was restored for the current context. */
  restored: boolean;
  /** Attribution of the currently-loaded draft (owner + last editor). */
  attribution: EmailDraftRecord | null;
  /**
   * Resume an arbitrary draft record (Team Drafts panel): loads its data
   * through the same onRestore path and retargets autosave at THAT draft
   * so edits update the shared doc instead of forking a private copy.
   */
  resumeDraft: (draft: EmailDraftRecord) => void;
  /** Delete the server draft and stop tracking until the content changes again. */
  discard: () => Promise<void>;
}

/**
 * Autosave/restore machinery shared by every composer surface.
 *
 * - On mount (and whenever kind/contextId changes) the server draft is
 *   loaded once and handed to `onRestore`.
 * - Whenever `snapshot` (a JSON-serialisable, memoised view of the form)
 *   diverges from the last known server state, it is saved after a short
 *   debounce. The snapshot captured at load time becomes the baseline,
 *   so untouched default content never creates a draft.
 * - A pending save is flushed (fire-and-forget) on unmount/context
 *   switch so closing the page mid-debounce doesn't lose the last edit.
 */
export function useEmailDraft(opts: {
  kind: EmailDraftKind;
  contextId: string | null;
  enabled?: boolean;
  snapshot: Record<string, any> | null;
  onRestore: (data: Record<string, any>, updatedAt: string) => void;
}): UseEmailDraftResult {
  const { kind, contextId, enabled = true, snapshot } = opts;
  const [status, setStatus] = useState<EmailDraftStatus>('idle');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);
  const [attribution, setAttribution] = useState<EmailDraftRecord | null>(null);

  const onRestoreRef = useRef(opts.onRestore);
  onRestoreRef.current = opts.onRestore;
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const baselineRef = useRef<string | null>(null);
  const loadedKeyRef = useRef<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Identity of the draft doc autosave writes to. For shared campaign
  // drafts this is the resumed/restored doc's id, so edits land on the
  // shared draft (last-writer-wins) instead of a private fork.
  const targetDraftIdRef = useRef<string | null>(null);

  const key = enabled && contextId ? `${kind}:${contextId}` : null;

  /* ── Load + restore ── */
  useEffect(() => {
    if (!key || !contextId) return;
    let cancelled = false;
    loadedKeyRef.current = null;
    baselineRef.current = null;
    targetDraftIdRef.current = null;
    setAttribution(null);
    setRestored(false);
    setSavedAt(null);
    setStatus('loading');
    // Race guard: if the user starts editing while the draft load is in
    // flight, the restore must NOT clobber their in-progress work when
    // it lands — their version wins and becomes the tracked baseline.
    const snapshotAtLoadStart =
      snapshotRef.current != null ? JSON.stringify(snapshotRef.current) : null;
    loadEmailDraft(kind, contextId)
      .then((draft) => {
        if (cancelled) return;
        const currentSnapshot =
          snapshotRef.current != null ? JSON.stringify(snapshotRef.current) : null;
        if (currentSnapshot !== snapshotAtLoadStart && draft) {
          baselineRef.current = currentSnapshot;
          loadedKeyRef.current = key;
          setStatus('idle');
          return;
        }
        if (draft && draft.data && Object.keys(draft.data).length > 0) {
          baselineRef.current = JSON.stringify(draft.data);
          targetDraftIdRef.current = draft.id || null;
          setAttribution(draft);
          setSavedAt(draft.updatedAt);
          setRestored(true);
          onRestoreRef.current(draft.data, draft.updatedAt);
        } else {
          // No draft: whatever the form currently holds is the baseline —
          // untouched defaults must not spawn a draft.
          baselineRef.current =
            snapshotRef.current != null ? JSON.stringify(snapshotRef.current) : null;
        }
        loadedKeyRef.current = key;
        setStatus(draft ? 'saved' : 'idle');
      })
      .catch(() => {
        if (cancelled) return;
        // Drafts are best-effort: a load failure must never block composing.
        baselineRef.current =
          snapshotRef.current != null ? JSON.stringify(snapshotRef.current) : null;
        loadedKeyRef.current = key;
        setStatus('idle');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  /* ── Debounced autosave ──
   * A null snapshot means "pristine form" (caller-defined): if a draft
   * exists on the server it is deleted — this is how send/reset flows
   * clean up their draft without explicit calls. */
  const snapshotJson = snapshot != null ? JSON.stringify(snapshot) : null;
  useEffect(() => {
    if (!key || !contextId) return;
    if (loadedKeyRef.current !== key) return; // wait for load/restore
    if (snapshotJson === baselineRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (snapshotJson == null) {
      if (baselineRef.current == null) return;
      saveTimerRef.current = setTimeout(() => {
        deleteEmailDraft(kind, contextId, targetDraftIdRef.current)
          .catch(() => undefined)
          .finally(() => {
            baselineRef.current = null;
            targetDraftIdRef.current = null;
            setAttribution(null);
            setSavedAt(null);
            setRestored(false);
            setStatus('idle');
          });
      }, 400);
      return () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      };
    }
    const jsonToSave = snapshotJson;
    saveTimerRef.current = setTimeout(() => {
      setStatus('saving');
      saveEmailDraft(kind, contextId, JSON.parse(jsonToSave), targetDraftIdRef.current)
        .then(({ updatedAt, draftId, draft }) => {
          baselineRef.current = jsonToSave;
          if (draftId) targetDraftIdRef.current = draftId;
          // Server-stamped attribution (incl. who just edited) keeps the
          // "last edited by" labels truthful after every autosave.
          if (draft) setAttribution(draft);
          setSavedAt(updatedAt);
          setStatus('saved');
        })
        .catch(() => setStatus('error'));
    }, 1200);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotJson, key]);

  /* ── Flush a pending save on unmount / context switch ── */
  useEffect(() => {
    return () => {
      if (!key || !contextId) return;
      if (loadedKeyRef.current !== key) return;
      const current = snapshotRef.current != null ? JSON.stringify(snapshotRef.current) : null;
      if (current != null && current !== baselineRef.current) {
        // Fire-and-forget: the page may be closing.
        saveEmailDraft(kind, contextId, JSON.parse(current), targetDraftIdRef.current).catch(() => undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  /* ── Resume an arbitrary draft (Team Drafts panel) ──
   * Retargets autosave at the given draft BEFORE handing its data to
   * onRestore, so the edits the admin makes next update that shared doc. */
  const resumeDraft = (draft: EmailDraftRecord) => {
    if (!contextId || !draft) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    targetDraftIdRef.current = draft.id || null;
    baselineRef.current = JSON.stringify(draft.data || {});
    setAttribution(draft);
    setSavedAt(draft.updatedAt || null);
    setRestored(true);
    setStatus('saved');
    loadedKeyRef.current = key;
    onRestoreRef.current(draft.data || {}, draft.updatedAt || '');
  };

  const discard = async () => {
    if (!contextId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    try {
      await deleteEmailDraft(kind, contextId, targetDraftIdRef.current);
    } catch {
      /* best-effort */
    }
    // Stop tracking the on-screen content until it changes again,
    // otherwise the autosave would immediately recreate the draft.
    baselineRef.current =
      snapshotRef.current != null ? JSON.stringify(snapshotRef.current) : null;
    targetDraftIdRef.current = null;
    setAttribution(null);
    setSavedAt(null);
    setRestored(false);
    setStatus('idle');
  };

  return { status, savedAt, restored, attribution, resumeDraft, discard };
}
